import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, FileText, Calendar, Trash2, Loader2, Eye, Clock, TrendingUp, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useContext } from "react";
import VotingButtons from "@/components/VotingButtons";
import NewsletterWithVoting from "@/components/NewsletterWithVoting";
import NewsletterChat from "@/components/NewsletterChat";
import { AuthContext } from "@/App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ArchivePage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [newsletters, setNewsletters] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [topRated, setTopRated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNewsletter, setSelectedNewsletter] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [newslettersRes, schedulesRes] = await Promise.all([
        axios.get(`${API}/newsletters`, { withCredentials: true }),
        axios.get(`${API}/schedules`, { withCredentials: true })
      ]);
      setNewsletters(newslettersRes.data);
      setSchedules(schedulesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await axios.delete(`${API}/schedules/${scheduleId}`, { withCredentials: true });
      toast.success("Schedule cancelled successfully");
      fetchData();
    } catch (error) {
      console.error("Error deleting schedule:", error);
      toast.error("Failed to cancel schedule");
    }
  };

  const handleViewNewsletter = (newsletter) => {
    setSelectedNewsletter(newsletter);
    setDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/60 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_insurecloud-news/artifacts/g99eghuw_Owl%20Ai%20animated%20Gif.gif" 
                alt="AI Newsletter Logo"
                className="h-10 w-10"
              />
              <div>
                <h1 className="text-xl font-bold text-slate-900 tracking-tight">AI Newsletter</h1>
                <p className="text-xs text-slate-500">Powered by Owl Intelligence</p>
              </div>
            </div>
            <Button
              data-testid="back-home-btn"
              onClick={() => navigate('/')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Tabs defaultValue="newsletters" className="w-full">
          <TabsList className="grid w-full max-w-3xl grid-cols-3 h-12 mb-8">
            <TabsTrigger data-testid="tab-newsletters" value="newsletters" className="text-base">
              <FileText className="mr-2 h-4 w-4" />
              All ({newsletters.length})
            </TabsTrigger>
            <TabsTrigger data-testid="tab-top-rated" value="top-rated" className="text-base">
              <TrendingUp className="mr-2 h-4 w-4" />
              Top Rated
            </TabsTrigger>
            <TabsTrigger data-testid="tab-schedules" value="schedules" className="text-base">
              <Calendar className="mr-2 h-4 w-4" />
              Schedules ({schedules.length})
            </TabsTrigger>
          </TabsList>

          {/* Newsletters Tab */}
          <TabsContent value="newsletters" data-testid="newsletters-list">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
              </div>
            ) : newsletters.length === 0 ? (
              <Card className="shadow-lg">
                <CardContent className="text-center py-20">
                  <FileText className="h-20 w-20 mx-auto mb-4 text-gray-300" />
                  <p className="text-xl text-gray-500">No newsletters generated yet</p>
                  <p className="text-sm text-gray-400 mt-2">Generate your first newsletter from the home page</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {newsletters.map((newsletter) => (
                  <Card key={newsletter.id} className="shadow-lg hover:shadow-xl transition-shadow border-none" data-testid={`newsletter-card-${newsletter.id}`}>
                    <CardHeader className="bg-gradient-to-r from-blue-50 to-teal-50">
                      <div className="flex justify-between items-start">
                        <Badge className="bg-blue-600 hover:bg-blue-700" data-testid={`newsletter-role-${newsletter.id}`}>{newsletter.role}</Badge>
                        <span className="text-xs text-gray-500">
                          {new Date(newsletter.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                      <CardTitle className="text-lg mt-3 line-clamp-2">{newsletter.prompt}</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-2">Focus Areas:</p>
                          <div className="flex flex-wrap gap-1">
                            {newsletter.focus_areas.map((area, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {area}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            data-testid={`view-newsletter-${newsletter.id}`}
                            onClick={() => handleViewNewsletter(newsletter)}
                            className="flex-1 bg-blue-600 hover:bg-blue-700"
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            View
                          </Button>
                          <Button
                            data-testid={`download-pdf-${newsletter.id}`}
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = `${API}/newsletters/${newsletter.id}/pdf`;
                              link.target = '_blank';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              toast.success("PDF download started!");
                            }}
                            variant="outline"
                            className="border-blue-300 text-blue-700 hover:bg-blue-50"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Top Rated Tab */}
          <TabsContent value="top-rated" data-testid="top-rated-list">
            <Card className="shadow-lg mb-6">
              <CardContent className="pt-6">
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-lg border border-yellow-200">
                  <h3 className="font-semibold text-yellow-900 mb-2">📊 Monthly Top Rated</h3>
                  <p className="text-sm text-yellow-800">
                    View the most upvoted newsletters of the month. Perfect for discovering the best insights shared by the community!
                  </p>
                </div>
              </CardContent>
            </Card>

            {loading ? (
              <div className="flex justify-center items-center py-20">
                <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {newsletters
                  .filter(n => (n.upvotes || 0) > 0)
                  .sort((a, b) => ((b.upvotes || 0) - (b.downvotes || 0)) - ((a.upvotes || 0) - (a.downvotes || 0)))
                  .slice(0, 12)
                  .map((newsletter, index) => (
                    <Card key={newsletter.id} className="shadow-lg hover:shadow-xl transition-shadow border-none relative" data-testid={`top-rated-${newsletter.id}`}>
                      {index < 3 && (
                        <div className="absolute -top-2 -right-2 bg-gradient-to-r from-yellow-400 to-orange-400 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold shadow-lg">
                          {index + 1}
                        </div>
                      )}
                      <CardHeader className="bg-gradient-to-r from-yellow-50 to-orange-50">
                        <div className="flex justify-between items-start">
                          <Badge className="bg-orange-600 hover:bg-orange-700">{newsletter.role}</Badge>
                          <div className="flex items-center gap-1 bg-white px-2 py-1 rounded">
                            <TrendingUp className="h-3 w-3 text-green-600" />
                            <span className="text-xs font-bold text-gray-700">
                              +{(newsletter.upvotes || 0) - (newsletter.downvotes || 0)}
                            </span>
                          </div>
                        </div>
                        <CardTitle className="text-lg mt-3 line-clamp-2">{newsletter.prompt}</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-4">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>{new Date(newsletter.timestamp).toLocaleDateString()}</span>
                            <VotingButtons
                              newsletterId={newsletter.id}
                              initialUpvotes={newsletter.upvotes || 0}
                              initialDownvotes={newsletter.downvotes || 0}
                              initialUserVote={newsletter.user_ratings?.[newsletter.user_id] || null}
                              onVoteChange={fetchData}
                            />
                          </div>
                          <Button
                            onClick={() => handleViewNewsletter(newsletter)}
                            className="w-full bg-orange-600 hover:bg-orange-700"
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            View Newsletter
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            )}
            
            {!loading && newsletters.filter(n => (n.upvotes || 0) > 0).length === 0 && (
              <Card className="shadow-lg">
                <CardContent className="text-center py-20">
                  <TrendingUp className="h-20 w-20 mx-auto mb-4 text-gray-300" />
                  <p className="text-xl text-gray-500">No rated newsletters yet</p>
                  <p className="text-sm text-gray-400 mt-2">Start voting on newsletters to see top rated content here</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Schedules Tab */}
          <TabsContent value="schedules" data-testid="schedules-list">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
              </div>
            ) : schedules.length === 0 ? (
              <Card className="shadow-lg">
                <CardContent className="text-center py-20">
                  <Calendar className="h-20 w-20 mx-auto mb-4 text-gray-300" />
                  <p className="text-xl text-gray-500">No active schedules</p>
                  <p className="text-sm text-gray-400 mt-2">Create a schedule from the home page</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {schedules.map((schedule) => (
                  <Card key={schedule.id} className="shadow-lg hover:shadow-xl transition-shadow border-none" data-testid={`schedule-card-${schedule.id}`}>
                    <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
                      <div className="flex justify-between items-start">
                        <Badge className="bg-purple-600 hover:bg-purple-700" data-testid={`schedule-role-${schedule.id}`}>{schedule.role}</Badge>
                        <Badge variant="outline" className="capitalize">
                          {schedule.frequency}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg mt-3 line-clamp-2">{schedule.prompt}</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="space-y-3">
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex items-center text-sm text-gray-700">
                            <Clock className="h-4 w-4 mr-2 text-gray-500" />
                            <span className="font-semibold">
                              {schedule.frequency === "weekly" && `${schedule.day_of_week}s at `}
                              {schedule.frequency === "daily" && "Daily at "}
                              {schedule.time}
                            </span>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-2">Focus Areas:</p>
                          <div className="flex flex-wrap gap-1">
                            {schedule.focus_areas.map((area, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {area}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <Button
                          data-testid={`delete-schedule-${schedule.id}`}
                          onClick={() => handleDeleteSchedule(schedule.id)}
                          variant="destructive"
                          className="w-full"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Cancel Schedule
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Newsletter Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">Newsletter Details</DialogTitle>
            <DialogDescription>
              Generated on {selectedNewsletter && new Date(selectedNewsletter.timestamp).toLocaleString()}
            </DialogDescription>
          </DialogHeader>
          {selectedNewsletter && (
            <div className="space-y-4" data-testid="newsletter-dialog-content">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-semibold text-blue-600 mb-1">Role</p>
                    <p className="text-sm text-blue-900">{selectedNewsletter.role}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-blue-600 mb-1">Topic</p>
                    <p className="text-sm text-blue-900">{selectedNewsletter.prompt}</p>
                  </div>
                </div>
              </div>
              
              <NewsletterWithVoting 
                newsletter={selectedNewsletter}
                user={user}
              />
              
              {/* AI Chat Feature */}
              <NewsletterChat
                newsletterContent={selectedNewsletter.content}
                newsletterTitle={selectedNewsletter.prompt}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArchivePage;